/**
 * WatchlistPage — dedicated page at /watchlist.
 *
 * Implements:
 *  - TC-009: Empty state with link to search when no schools saved.
 *  - TC-013: Remove button is keyboard-accessible (visible focus, Enter/Space).
 *
 * WI-44 changes:
 *  - "Show scores for:" section removed (AC-8, AC-15).
 *  - Position/year selectors, prefsLoaded, getPreferences, handlePositionChange,
 *    handleYearChange, and setPreferences calls are all removed (AC-8, AC-15).
 *  - GET /api/v1/watchlist is called once on mount with no query params (AC-9).
 *  - WatchlistCard builds its school link from per-row sport_slug / target_year /
 *    position_slugs (AC-10, AC-11).
 *  - Subtitle context label: "<SPORT> · <POSITION_LIST> · <YEAR>" (AC-12).
 *
 * Design reference: /Users/andrewtuliszewski/IdeaProjects/openrosterscout/project/designs/06-watchlist.md
 *
 * Score label format (per design): "72 · HIGH · MID/2027"
 * Null score (Open Question 2): "— (data not available for this position/year)"
 * Freshness label: "Last updated N days ago" from scraped_at.
 *
 * Remove behavior: immediate remove (v1 baseline).
 * P2 undo window is explicitly out of scope for this work item.
 */

import React, { useCallback, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { WatchlistEntry } from '../api/watchlist';
import { Badge } from './ui/Badge';
import { ScoreBadge } from './ScoreBadge';
import { ErrorBanner } from './ui/ErrorBanner';
import { SkeletonRow } from './ui/SkeletonRow';
import { AppHeader } from './AppHeader';

// P3: empty state icon — bookmark outline SVG, aria-hidden (decorative).
function BookmarkEmptyIcon() {
  return (
    <svg
      aria-hidden="true"
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-300 mb-4"
    >
      <path
        d="M10 8C10 6.34 11.34 5 13 5H35C36.66 5 38 6.34 38 8V43L24 35L10 43V8Z"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Sport slug → display label mapping ────────────────────────────────────────

const SPORT_DISPLAY_LABELS: Record<string, string> = {
  soccer_mens: "MEN'S SOCCER",
  soccer_womens: "WOMEN'S SOCCER",
  football_mens: 'FOOTBALL',
};

function sportDisplayLabel(slug: string): string {
  return SPORT_DISPLAY_LABELS[slug] ?? slug.replace(/_/g, ' ').toUpperCase();
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface WatchlistCardProps {
  entry: WatchlistEntry;
  onRemove: (schoolId: string) => void;
  removing: boolean;
}

function WatchlistCard({ entry, onRemove, removing }: WatchlistCardProps) {
  const hasScore = entry.score !== null && entry.tier !== null;

  // WI-44 AC-10: build the school link from per-row context when present.
  const schoolLink = (() => {
    if (!entry.sport_slug) {
      // No context — link to picker (current behavior).
      return `/schools/${entry.slug}?from=watchlist`;
    }
    const params = new URLSearchParams();
    params.set('sport', entry.sport_slug);
    if (entry.target_year) params.set('target_year', String(entry.target_year));
    if (entry.position_slugs && entry.position_slugs.length > 0) {
      params.set('position', entry.position_slugs.join(','));
    }
    params.set('from', 'watchlist');
    return `/schools/${entry.slug}?${params.toString()}`;
  })();

  // WI-44 AC-12: subtitle context label — "<SPORT> · <POSITION_LIST> · <YEAR>"
  const contextLabel = (() => {
    if (!entry.sport_slug) return null;
    const sportPart = sportDisplayLabel(entry.sport_slug);
    const yearPart = entry.target_year ? String(entry.target_year) : null;
    const positionPart =
      entry.position_slugs && entry.position_slugs.length > 0
        ? entry.position_slugs.map((s) => s.toUpperCase()).join(', ')
        : null;

    const segments = [sportPart, positionPart, yearPart].filter(Boolean);
    return segments.join(' · ');
  })();

  // v2: hairline-row layout — no card border, no rounded corners, no shadow.
  // Parent <ul> uses divide-y divide-hairline to separate rows.
  return (
    <article
      className={`px-4 py-3 focus-within:ring-2 focus-within:ring-brand-primary motion-safe:transition-opacity ${removing ? 'opacity-50' : 'opacity-100'}`}
      aria-label={`${entry.name}${hasScore ? `, score ${Math.round(entry.score!)}` : ', no score available'}`}
    >
      <div className="flex items-center justify-between gap-3">
        {/* School info */}
        <div className="flex-1 min-w-0">
          {/* v2: text-[17px] for dominant school name hierarchy */}
          <h2 className="text-[17px] font-semibold text-gray-900 leading-tight truncate">
            <Link
              to={schoolLink}
              className="hover:text-brand-primary-dk hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
              aria-label={`View roster detail for ${entry.name}`}
            >
              {entry.name}
            </Link>
          </h2>
          {/* v2: text-xs meta line (from text-sm) */}
          {/* WCAG override: text-gray-500 on surface-base = 4.44:1 FAIL AA; using text-gray-600 = 6.94:1 PASS */}
          <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <Badge className="mr-0.5">{entry.division_name}</Badge>
            {entry.region} &middot; {entry.city}, {entry.state}
            {contextLabel && (
              <>
                <span className="text-gray-400" aria-hidden="true">·</span>
                <span
                  className="text-gray-500 font-medium"
                  aria-label={`Saved context: ${contextLabel}`}
                >
                  {contextLabel}
                </span>
              </>
            )}
          </p>
        </div>

        {/* Score + remove */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* WI-26 three-state score rendering:
              State 1 (has_data=true OR no position): normal ScoreBadge.
              State 2 (has_data=false, fallback present): muted gray badge with ~ prefix.
              State 3 (no score): "No score yet" placeholder. */}
          {hasScore && entry.has_data === false && entry.fallback_score ? (
            // WI-26 State 2: coarse fallback
            <div className="flex flex-col items-end gap-0.5">
              <span
                className="inline-flex items-center px-2.5 py-1 rounded bg-gray-100 border border-gray-300 text-gray-500 text-lg font-bold tabular-nums"
                aria-label={`Score shown is the ${entry.fallback_score.position_name} position estimate; no fine-position data available`}
              >
                ~{Math.round(entry.fallback_score.score)}
              </span>
              <p className="text-xs text-gray-500 mt-0.5" aria-hidden="true">
                {entry.fallback_score.position_name} score · no fine-position data
              </p>
            </div>
          ) : hasScore ? (
            // State 1: normal ScoreBadge (has_data=true or no position filter)
            <ScoreBadge
              score={Math.round(entry.score!)}
              tier={entry.tier!}
            />
          ) : (
            // State 3: no score
            <span className="text-xs text-gray-400" aria-label="Score not available for this position and year">
              — <span className="text-gray-400">No score yet</span>
            </span>
          )}

          {/* Remove button — TC-013: keyboard-accessible, 44px touch target */}
          <button
            type="button"
            onClick={() => onRemove(entry.school_id)}
            disabled={removing}
            className="flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 motion-safe:transition-colors"
            aria-label={`Remove ${entry.name} from watchlist`}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      </div>
    </article>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function WatchlistPage() {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;

  // WI-44 AC-9: refresh() is called with no args on mount — no global position/year.
  const { entries, loading: watchlistLoading, remove } = useWatchlist(isAuthenticated);

  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleRemove = useCallback(
    async (schoolId: string) => {
      setRemovingIds((prev) => { const next = new Set(prev); next.add(schoolId); return next; });
      setRemoveError(null);
      try {
        await remove(schoolId);
      } catch {
        setRemovingIds((prev) => {
          const next = new Set(prev);
          next.delete(schoolId);
          return next;
        });
        setRemoveError("Couldn't remove that school. Please try again.");
      }
    },
    [remove]
  );

  // Wait for auth check before deciding what to render.
  if (authLoading) {
    return (
      <div className="min-h-screen bg-surface-base">
        <AppHeader isAuthenticated={true} />
        <main className="max-w-3xl mx-auto px-4 py-12 text-center">
          <SkeletonRow height="h-8" className="w-48 mx-auto" />
        </main>
      </div>
    );
  }

  // Redirect unauthenticated users to login (with return URL).
  if (!isAuthenticated) {
    return <Navigate to={`/login?return=/watchlist`} replace />;
  }

  return (
    <div className="min-h-screen bg-surface-base pb-16">
      <AppHeader isAuthenticated={true} />

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">

        {/* P3: page title + count — flex row on desktop */}
        <div className="flex items-baseline justify-between gap-2">
          <h1 className="text-2xl font-bold text-gray-900">My Watchlist</h1>
          {!watchlistLoading && entries.length > 0 && (
            // WCAG: text-gray-500 on surface-base = 4.44:1 FAIL AA; using text-gray-600 = 6.94:1 PASS
            <span className="text-sm text-gray-600 tabular-nums whitespace-nowrap">
              {entries.length} school{entries.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Error banner for remove failure */}
        {removeError && (
          <ErrorBanner
            message={removeError}
            onDismiss={() => setRemoveError(null)}
          />
        )}

        {/* Loading state */}
        {watchlistLoading && (
          <div
            className="space-y-3"
            aria-busy="true"
            aria-label="Loading watchlist"
          >
            {[1, 2, 3].map((i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        )}

        {/* TC-009: Empty state — P3: bookmark SVG icon replaces ☆ emoji */}
        {!watchlistLoading && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <BookmarkEmptyIcon />
            <h2 className="text-base font-semibold text-gray-700 mb-2">Your watchlist is empty.</h2>
            <p className="text-gray-500 text-sm mb-6 max-w-xs">
              Save schools that look promising and come back to compare them.
            </p>
            <Link
              to="/"
              className="bg-brand-primary text-white px-6 py-2.5 rounded-lg hover:bg-brand-primary-dk motion-safe:transition-colors font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
            >
              Find schools
            </Link>
          </div>
        )}

        {/* Watchlist */}
        {!watchlistLoading && entries.length > 0 && (
          /* TC-013: keyboard-navigable list */
          /* v2: divide-y divide-hairline — hairline-row pattern (change 3) */
          <ul
            className="divide-y divide-hairline"
            aria-label={`${entries.length} saved schools`}
          >
            {entries.map((entry) => (
              <li key={entry.school_id}>
                <WatchlistCard
                  entry={entry}
                  onRemove={handleRemove}
                  removing={removingIds.has(entry.school_id)}
                />
              </li>
            ))}
          </ul>
        )}

      </main>
    </div>
  );
}

// ── Shared page header ────────────────────────────────────────────────────────

// PageHeader removed — replaced by shared AppHeader component (WI-23b).
